import React, { useEffect, useState, useRef } from "react";
import {
  useSearchParams,
  useOutletContext,
  useNavigate,
  useLocation,
} from "react-router-dom";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";
import {
  getHomeworkById,
  updateHomework,
  getCompletedStudents,
  getCompletedStudent,
} from "../../../helper/apicalls/homework";
import CommonTable from "../../common/commonTable";
import { AddIcon, EditIcon, DeleteIcon, ArrowBackIcon } from "../../../icons";
import "../../../styles/teacher/HomeworkDetail.scss";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import FeedbackPopup from "../../common/FeedbackPopup";
import Pagination from "../../common/Pagination";
const HomeworkDetail = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const batchId = searchParams.get("batch");
  const courseKey = searchParams.get("course");
  const subjectKey = searchParams.get("subject");
  const topicKey = searchParams.get("topic");
  const subtopicKey = searchParams.get("subtopic");
  const view = searchParams.get("view");
  const level = searchParams.get("level");
  const completedId = searchParams.get("cid");
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderName, setIsShowSearch } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [homework, setHomework] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [completedStudents, setCompletedStudents] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [completedError, setCompletedError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ submission: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const searchDebounceRef = useRef(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentResult, setStudentResult] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState("");
  const [studentQuestions, setStudentQuestions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [qEdit, setQEdit] = useState(false);
  const [qDraft, setQDraft] = useState([]);
  const [titleDraft, setTitleDraft] = useState("");
  const [deadlineDraft, setDeadlineDraft] = useState("");
  const [viewerVersion, setViewerVersion] = useState(0);
  const [saveNonce, setSaveNonce] = useState(0);
  const [cancelNonce, setCancelNonce] = useState(0);
  const [batchSaveId, setBatchSaveId] = useState(null);
  const [batchExpected, setBatchExpected] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [batchAccum, setBatchAccum] = useState({});
  const [editIndex, setEditIndex] = useState(-1);
  const [editForm, setEditForm] = useState({
    question: "",
    options: ["", "", "", ""],
    correctIndex: 0,
    explanation: "",
  });

  const isDeadlineOver = (() => {
    if (!homework?.deadline) return false;
    try {
      const deadlineDate = new Date(homework.deadline);
      if (Number.isNaN(deadlineDate.getTime())) return false;
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(homework.deadline.trim());
      if (isDateOnly) {
        deadlineDate.setHours(23, 59, 59, 999);
      }
      return new Date() > deadlineDate;
    } catch (_) {
      return false;
    }
  })();

  const getAnswerText = (ans, options) => {
    if (ans === undefined || ans === null || String(ans).trim() === "")
      return "-";
    if (!options) return String(ans);

    const normalizedAnsKey = String(ans).trim().toUpperCase();

    if (Array.isArray(options)) {
      for (const opt of options) {
        if (opt && typeof opt === "object") {
          const keys = Object.keys(opt);
          if (keys.length === 1) {
            const key = keys[0];
            if (key.toUpperCase() === normalizedAnsKey) {
              return opt[key];
            }
            if (
              String(opt[key]).trim().toLowerCase() ===
              String(ans).trim().toLowerCase()
            ) {
              return opt[key];
            }
          } else {
            const key = opt.key || opt.label || "";
            const val = opt.text || opt.option || opt.value || opt.answer || "";
            if (
              String(key).toUpperCase() === normalizedAnsKey ||
              String(val).toLowerCase() === String(ans).toLowerCase()
            ) {
              return val;
            }
          }
        } else if (typeof opt === "string") {
          if (opt.toLowerCase() === ans.toLowerCase()) {
            return opt;
          }
        }
      }

      const idx = parseInt(ans, 10);
      if (!isNaN(idx) && idx >= 0 && idx < options.length) {
        const opt = options[idx];
        if (opt && typeof opt === "object") {
          const keys = Object.keys(opt);
          if (keys.length === 1) return opt[keys[0]];
          return (
            opt.text ||
            opt.option ||
            opt.value ||
            opt.answer ||
            JSON.stringify(opt)
          );
        }
        return String(opt);
      }
    } else if (typeof options === "object") {
      if (options[normalizedAnsKey] !== undefined) {
        return options[normalizedAnsKey];
      }
      for (const k of Object.keys(options)) {
        if (k.toUpperCase() === normalizedAnsKey) {
          return options[k];
        }
        if (
          String(options[k]).trim().toLowerCase() ===
          String(ans).trim().toLowerCase()
        ) {
          return options[k];
        }
      }
    }

    return String(ans);
  };

  const fetchHomework = async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getHomeworkById(id);
      setHomework(data?.data || data || null);
    } catch (e) {
      console.error("Failed to fetch homework by id:", e);
      setError("Failed to load homework");
      setHomework(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHeaderName && setHeaderName("Homework Details");
    setIsShowSearch && setIsShowSearch(false);
  }, [setHeaderName, setIsShowSearch]);

  useEffect(() => {
    const isCompletedView =
      view === "completed" ||
      Boolean(completedId) ||
      location.pathname.includes("/completed");
    if (!isCompletedView) {
      fetchHomework();
    }
  }, [id, view, completedId, location.pathname]);

  const fetchCompleted = async (currentPage = page, currentLimit = limit) => {
    if (
      (view !== "completed" && !location.pathname.includes("/completed")) ||
      !id
    )
      return;
    setCompletedLoading(true);
    setCompletedError("");
    try {
      const submission = (filters?.submission || "").trim();
      const submittedParam =
        submission === "yes" ? true : submission === "no" ? false : undefined;
      const res = await getCompletedStudents({
        level: level || "course",
        id,
        search: (searchTerm || "").trim() || undefined,
        submitted: submittedParam,
        page: currentPage,
        limit: currentLimit,
      });
      if (res && Array.isArray(res.data)) {
        setCompletedStudents(res.data);
        setTotalPages(Number(res.total_pages) || 1);
      } else {
        setCompletedStudents(Array.isArray(res) ? res : []);
        setTotalPages(1);
      }
    } catch (_) {
      setCompletedError("Failed to load completed students");
      setCompletedStudents([]);
    } finally {
      setCompletedLoading(false);
    }
  };

  useEffect(() => {
    fetchCompleted(page, limit);
  }, [view, level, id, searchTerm, filters, location.pathname, page, limit]);

  const handleSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(val || "");
      setPage(1);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchOne = async () => {
      if (!completedId) {
        setSelectedStudent(null);
        setStudentQuestions([]);
        setStudentResult(null);
        return;
      }
      setSelectedStudent({});
      setStudentLoading(true);
      setStudentError("");
      setStudentQuestions([]);
      setStudentResult(null);
      try {
        const res = await getCompletedStudent({ id: completedId });
        setStudentResult(res);
        const raw = Array.isArray(res?.attempted_data)
          ? res.attempted_data
          : Array.isArray(res?.attempt_data)
            ? res.attempt_data
            : Array.isArray(res?.questions)
              ? res.questions
              : Array.isArray(res?.data?.questions)
                ? res.data.questions
                : Array.isArray(res?.result?.questions)
                  ? res.result.questions
                  : [];
        const normalized = (raw || []).map((item) => {
          const originalType = String(
            item?.type || item?.question_type || item?.kind || "",
          ).toLowerCase();
          return {
            ...(item || {}),
            type:
              originalType ||
              ((!item?.options || item.options.length === 0) &&
              ["true", "false", true, false].includes(
                String(item?.answer ?? "").toLowerCase(),
              )
                ? "trueorfalse"
                : "mcq"),
          };
        });
        setStudentQuestions(normalized);
      } catch (_) {
        setStudentError("Failed to load student details");
        setStudentQuestions([]);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchOne();
  }, [completedId]);

  const handleStudentClick = (row) => {
    if (!row?.homework_completed_id) return;
    const qp = new URLSearchParams(searchParams);
    qp.set("cid", String(row.homework_completed_id));
    let base = "/teacher/homework/completed";
    if (courseKey && subjectKey && topicKey && subtopicKey)
      base = `/teacher/subtopic/homework/completed`;
    else if (courseKey && subjectKey && topicKey)
      base = `/teacher/topic/homework/completed`;
    else if (courseKey && subjectKey)
      base = `/teacher/subject/homework/completed`;
    else if (courseKey) base = `/teacher/course/homework/completed`;
    navigate(`${base}?${qp.toString()}`);
  };

  useEffect(() => {
    const qd = (() => {
      if (!homework) return [];
      if (Array.isArray(homework.questions)) return homework.questions;
      if (homework?.content) return homework.content;
      if (homework?.data?.questions) return homework.data.questions;
      return [];
    })();
    setQuestions(Array.isArray(qd) ? qd : []);
    setQDraft(Array.isArray(qd) ? JSON.parse(JSON.stringify(qd)) : []);
    const hwTitle = homework?.title || homework?.name || "";
    const hwDeadline = (() => {
      if (!homework?.deadline) return "";
      try {
        const d = new Date(homework.deadline);
        if (Number.isNaN(d.getTime())) return "";
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      } catch (_) {
        return "";
      }
    })();
    setTitleDraft(hwTitle);
    setDeadlineDraft(hwDeadline);
  }, [homework]);

  const beginQEdit = () => {
    setQDraft(JSON.parse(JSON.stringify(questions || [])));
    const hwTitle = homework?.title || homework?.name || "";
    setTitleDraft(hwTitle);
    const hwDeadline = (() => {
      if (!homework?.deadline) return "";
      try {
        const d = new Date(homework.deadline);
        if (Number.isNaN(d.getTime())) return "";
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      } catch (_) {
        return "";
      }
    })();
    setDeadlineDraft(hwDeadline);
    setQEdit(true);
  };

  useEffect(() => {
    const editParam = searchParams.get("edit");
    if (
      view === "completed" ||
      completedId ||
      location.pathname.includes("/completed")
    )
      return;
    if (isDeadlineOver) return;
    if (!qEdit && (editParam === "1" || editParam === "true")) {
      beginQEdit();
    }
  }, [searchParams, location.pathname, isDeadlineOver]);

  const cancelQEdit = () => {
    setQDraft(JSON.parse(JSON.stringify(questions || [])));
    setQEdit(false);
    const hwTitle = homework?.title || homework?.name || "";
    setTitleDraft(hwTitle);
    const hwDeadline = (() => {
      if (!homework?.deadline) return "";
      try {
        const d = new Date(homework.deadline);
        if (Number.isNaN(d.getTime())) return "";
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      } catch (_) {
        return "";
      }
    })();
    setDeadlineDraft(hwDeadline);
  };

  const saveQEdit = async () => {
    await persistQuestions(qDraft || []);
    setQEdit(false);
  };

  const normalizeType = (q) => {
    const t = String(
      q?.type || q?.question_type || q?.kind || "",
    ).toLowerCase();
    if (t) return t;
    const ans = String(q?.answer ?? q?.correct ?? "").toLowerCase();
    const hasTF = ["true", "false", "1", "0", true, false].includes(ans);
    const hasOptions = Array.isArray(q?.options) || Array.isArray(q?.choices);
    return !hasOptions && hasTF ? "trueorfalse" : "mcq";
  };

  const getOptions = (q) =>
    Array.isArray(q?.options)
      ? q.options
      : Array.isArray(q?.choices)
        ? q.choices
        : [];

  const setQuestionField = (idx, updater) => {
    setQDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev || []));
      next[idx] = updater(next[idx] || {});
      return next;
    });
  };

  const setOptionText = (qIdx, optIdx, text) => {
    setQDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev || []));
      const q = next[qIdx] || {};
      const opts = getOptions(q);
      const ensureLabel = (i, old) => {
        if (old && typeof old === "object") {
          const keys = Object.keys(old);
          const key = keys.length === 1 ? keys[0] : String.fromCharCode(65 + i);
          return { [key]: text };
        }
        return { [String.fromCharCode(65 + i)]: text };
      };
      if (Array.isArray(q.options) || (!q.choices && !q.options)) {
        q.options = [...opts];
        q.options[optIdx] = ensureLabel(optIdx, q.options[optIdx]);
      } else {
        q.choices = [...opts];
        q.choices[optIdx] = ensureLabel(optIdx, q.choices[optIdx]);
      }
      next[qIdx] = q;
      return next;
    });
  };

  const addOption = (qIdx) => {
    setQDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev || []));
      const q = next[qIdx] || {};
      const opts = getOptions(q);
      const label = String.fromCharCode(65 + opts.length);
      const newOpt = { [label]: "" };
      if (Array.isArray(q.options) || (!q.choices && !q.options)) {
        q.options = [...opts, newOpt];
      } else {
        q.choices = [...opts, newOpt];
      }
      next[qIdx] = q;
      return next;
    });
  };

  const deleteOption = (qIdx, optIdx) => {
    setQDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev || []));
      const q = next[qIdx] || {};
      const opts = getOptions(q);
      if (opts.length <= 2) return next;
      const newOpts = opts.filter((_, i) => i !== optIdx);
      if (Array.isArray(q.options) || (!q.choices && !q.options))
        q.options = newOpts;
      else q.choices = newOpts;
      const answer = q.answer || q.correct || "";
      const flattened = newOpts.map((o) =>
        typeof o === "object" ? Object.values(o)[0] : String(o),
      );
      if (!flattened.includes(answer)) q.answer = "";
      next[qIdx] = q;
      return next;
    });
  };

  const extractQuestionsFromViewerItem = (item) => {
    try {
      if (!item) return [];
      if (item.content && typeof item.content === "string") {
        const parsed = JSON.parse(item.content);
        if (parsed && Array.isArray(parsed.questions)) return parsed.questions;
        if (parsed && (parsed.pairs || parsed.key)) return [parsed];
      } else if (item.content && typeof item.content === "object") {
        if (Array.isArray(item.content.questions))
          return item.content.questions;
        if (item.content && (item.content.pairs || item.content.key))
          return [item.content];
      }
      if (Array.isArray(item.questions)) return item.questions;
      return [item];
    } catch (_) {
      return [];
    }
  };

  const persistQuestions = async (nextQs) => {
    if (!id) return;
    try {
      setLoading(true);
      const payload = { id, content: nextQs };
      if (qEdit) {
        if (titleDraft && titleDraft.trim()) {
          payload.name = titleDraft.trim();
          payload.title = titleDraft.trim();
        }
        if (deadlineDraft) {
          payload.deadline = deadlineDraft;
        }
      }
      const res = await updateHomework(payload);
      const updated = res?.data || res || {};
      setHomework((prev) => ({
        ...(prev || {}),
        ...(updated || {}),
        questions: nextQs,
        ...(qEdit && titleDraft ? { name: titleDraft, title: titleDraft } : {}),
        ...(qEdit && deadlineDraft ? { deadline: deadlineDraft } : {}),
      }));
      setQuestions(nextQs);
    } catch (e) {
      setError("Failed to update homework");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOne = async (idx) => {
    const next = questions.filter((_, i) => i !== idx);
    await persistQuestions(next);
  };

  const toDisplayText = (opt) => {
    if (typeof opt === "string") return opt;
    if (opt && typeof opt === "object") {
      const keys = Object.keys(opt);
      if (keys.length === 1) return String(Object.values(opt)[0] ?? "");
      return (
        opt.text || opt.option || opt.value || opt.answer || JSON.stringify(opt)
      );
    }
    return String(opt ?? "");
  };

  // const openEdit = (idx) => {
  //   const q = questions[idx] || {};
  //   const qText = q.question || q.title || q.text || "";
  //   const optsRaw = q.options || q.choices || [];
  //   const opts = Array.isArray(optsRaw) ? optsRaw.map(toDisplayText) : [];
  //   const answerText = q.answer || q.correct || "";
  //   const correctIndex = Math.max(
  //     0,
  //     opts.findIndex((t) => t === answerText),
  //   );
  //   setEditIndex(idx);
  //   setEditForm({
  //     question: qText,
  //     options: opts.length > 0 ? opts : ["", "", "", ""],
  //     correctIndex,
  //     explanation: q.explanation || "",
  //   });
  // };

  const reconstructFromEdit = (original, form) => {
    const out = JSON.parse(JSON.stringify(original || {}));
    const label = (n) => String.fromCharCode(65 + n);
    const labeled = (text, idx) => ({ [label(idx)]: String(text ?? "") });
    const opts = (form.options || []).map((t, i) => labeled(t, i));

    const hasChoices = "choices" in out;
    if ("question" in out) out.question = form.question || "";
    else if ("title" in out) out.title = form.question || "";
    else if ("text" in out) out.text = form.question || "";
    else out.question = form.question || "";

    if (hasChoices) out.choices = opts;
    else out.options = opts;

    const resolvedAnswer = (form.options || [])[form.correctIndex] || "";
    if ("answer" in out) out.answer = resolvedAnswer;
    else if ("correct" in out) out.correct = resolvedAnswer;
    else out.answer = resolvedAnswer;

    if (form.explanation !== undefined) out.explanation = form.explanation;
    return out;
  };

  const saveEdit = async () => {
    if (editIndex < 0) return setEditOpen(false);
    const original = questions[editIndex] || {};
    const updatedQ = reconstructFromEdit(original, editForm);
    const next = questions.map((q, i) => (i === editIndex ? updatedQ : q));
    await persistQuestions(next);
    setEditOpen(false);
    setEditIndex(-1);
  };

  if (!id && !completedId)
    return <EmptyPage content={"No homework id provided"} />;

  // const questionData = (() => {
  //   if (!homework) return null;
  //   if (Array.isArray(homework.questions)) return homework.questions;
  //   if (homework?.content) return homework.content;
  //   if (homework?.data?.questions) return homework.data.questions;
  //   return null;
  // })();

  return (
    <div className="homework-detail-container">
      <GlobalLoader visible={loading} text="Loading..." />
      {!loading && error && <EmptyPage content={error} />}
      {!loading &&
        !error &&
        !homework &&
        !(
          view === "completed" ||
          completedId ||
          location.pathname.includes("/completed")
        ) && <EmptyPage content={"No data found"} />}
      {!loading &&
        (homework ||
          view === "completed" ||
          completedId ||
          location.pathname.includes("/completed")) && (
          <div className="hd-card">
            <div className="hd-card-header">
              <span className="hd-title">
                <input
                  type="text"
                  className="hd-title-input"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  placeholder="Title"
                  style={{ minWidth: 240 }}
                  disabled={!qEdit}
                />
              </span>
              {!(
                view === "completed" ||
                completedId ||
                location.pathname.includes("/completed")
              ) && (
                <div className="hd-card-actions action-buttons-container">
                  <div className="hd-badges">
                    <input
                      type="date"
                      className="hd-deadline-input"
                      value={deadlineDraft}
                      onChange={(e) => setDeadlineDraft(e.target.value)}
                      disabled={!qEdit}
                    />
                    <span className="hd-badge hd-assigned">
                      Assigned:{" "}
                      {Array.isArray(homework?.users)
                        ? homework.users.length
                        : 0}
                    </span>
                    {isDeadlineOver && (
                      <span
                        className="hd-badge hd-deadline"
                        style={{
                          backgroundColor: "rgba(244, 67, 54, 0.08)",
                          color: "#d32f2f",
                          borderColor: "rgba(244, 67, 54, 0.3)",
                        }}
                      >
                        Deadline Over
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="action-button"
                    disabled={isDeadlineOver}
                    style={
                      isDeadlineOver
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : {}
                    }
                    onClick={() => {
                      if (isDeadlineOver) return;
                      const qp = new URLSearchParams();
                      if (id) qp.set("homework", id);
                      if (batchId) qp.set("batch", batchId);
                      let path = "/teacher/homework/assign";
                      if (courseKey && subjectKey && topicKey && subtopicKey) {
                        path = `/teacher/homework/course/${courseKey}/subject/${subjectKey}/topic/${topicKey}/subtopic/${subtopicKey}/assign`;
                      } else if (courseKey && subjectKey && topicKey) {
                        path = `/teacher/homework/course/${courseKey}/subject/${subjectKey}/topic/${topicKey}/assign`;
                      } else if (courseKey && subjectKey) {
                        path = `/teacher/homework/course/${courseKey}/subject/${subjectKey}/assign`;
                      } else if (courseKey) {
                        path = `/teacher/homework/course/${courseKey}/assign`;
                      }
                      navigate(`${path}?${qp.toString()}`);
                    }}
                    title={isDeadlineOver ? "Deadline has passed" : "Assign"}
                  >
                    <AddIcon className="action-button-icon" />
                    <span>Assign</span>
                  </button>
                  {!qEdit && (
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => {
                        const qp = new URLSearchParams(searchParams);
                        qp.set("view", "completed");
                        navigate(`?${qp.toString()}`);
                      }}
                    >
                      <span>Submission Status</span>
                    </button>
                  )}
                  {!qEdit ? (
                    <button
                      type="button"
                      className="action-button"
                      disabled={isDeadlineOver}
                      style={
                        isDeadlineOver
                          ? { opacity: 0.5, cursor: "not-allowed" }
                          : {}
                      }
                      onClick={() => {
                        if (isDeadlineOver) return;
                        beginQEdit();
                      }}
                      aria-label="Edit"
                      title={isDeadlineOver ? "Deadline has passed" : "Edit"}
                    >
                      <EditIcon className="action-button-icon" />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => {
                          const current = qEdit ? qDraft : questions;
                          setBatchAccum({});
                          setBatchCount(0);
                          setBatchExpected((current || []).length);
                          setBatchSaveId(Date.now());
                          setSaveNonce((n) => n + 1);
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => {
                          setQEdit(false);
                          setCancelNonce((n) => n + 1);
                          setViewerVersion((v) => v + 1);
                        }}
                        aria-label="Cancel"
                        title="Cancel"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              )}
              {(view === "completed" ||
                completedId ||
                location.pathname.includes("/completed")) && (
                <div className="hd-card-actions action-buttons-container">
                  {completedId ? (
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => {
                        const qp = new URLSearchParams(searchParams);
                        qp.delete("cid");
                        qp.set("view", "completed");
                        const newPath = location.pathname.replace(
                          "/completed",
                          "/detail",
                        );
                        navigate(`${newPath}?${qp.toString()}`);
                      }}
                    >
                      <ArrowBackIcon className="action-button-icon" />
                      <span>Back to Submissions</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => {
                        const qp = new URLSearchParams(searchParams);
                        qp.delete("view");
                        navigate(`?${qp.toString()}`);
                      }}
                    >
                      <span>View Questions</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="hd-content">
              {homework?.description && (
                <p style={{ whiteSpace: "pre-wrap" }}>{homework.description}</p>
              )}
              {homework?.batch && (
                <p>
                  <strong>Batch:</strong> {homework.batch}
                </p>
              )}
              {view === "completed" ||
              completedId ||
              location.pathname.includes("/completed") ? (
                <div>
                  <GlobalLoader visible={completedLoading} text="Loading..." />
                  {!completedLoading && completedError && (
                    <EmptyPage content={completedError} />
                  )}
                  {!completedError && !completedId && (
                    <>
                      <CommonTable
                        data={completedStudents}
                        columns={[
                          {
                            field: "name",
                            label: "Name",
                            render: (_v, r) => r?.name || r?.email || "Student",
                          },
                          {
                            field: "email",
                            label: "Email",
                            render: (_v, r) => r?.email || "-",
                          },
                          {
                            field: "register_no",
                            label: "Register No.",
                            align: "center",
                            render: (_v, r) => r?.register_no || "-",
                          },
                          {
                            field: "marks",
                            label: "Marks",
                            align: "center",
                            render: (_v, r) => {
                              if (!r?.submission) return "-";
                              const correct = r?.no_of_question_correct ?? 0;
                              const total = r?.no_of_questions_attempted ?? 0;
                              return `${correct} / ${total}`;
                            },
                          },
                          {
                            field: "submission",
                            label: "Submitted",
                            align: "center",
                            render: (_v, r) => (
                              <span
                                className={`hd-badge ${r?.submission ? "hd-assigned" : "hd-deadline"}`}
                              >
                                {r?.submission ? "Yes" : "No"}
                              </span>
                            ),
                          },
                        ]}
                        getRowId={(r) => r?.id}
                        onRowClick={handleStudentClick}
                        showToolbar
                        title={"Completed Students"}
                        showSearch
                        onSearchChange={handleSearchChange}
                        showFilter
                        filterSections={[
                          {
                            title: "Submitted",
                            key: "submission",
                            type: "select",
                            options: [
                              { label: "All", value: "" },
                              { label: "Yes", value: "yes" },
                              { label: "No", value: "no" },
                            ],
                          },
                        ]}
                        filterValues={filters}
                        onFilterChange={(newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }}
                        maxBodyHeight={`calc(-350px + 100vh)`}
                        fixedBodyRows={8}
                      />
                      <div
                        className="student-pagination"
                        style={{
                          marginTop: 16,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Pagination
                          page={page}
                          pageCount={totalPages}
                          onChange={(p) => setPage(p)}
                          perPage={limit}
                          perPageOptions={[5, 10, 25, 100]}
                          onPerPageChange={(pp) => {
                            setLimit(pp);
                            setPage(1);
                          }}
                        />
                      </div>
                    </>
                  )}
                  {(completedId || selectedStudent) && (
                    <div style={{ marginTop: 16 }}>
                      <GlobalLoader
                        visible={studentLoading}
                        text="Loading..."
                      />
                      {!studentLoading && studentError && (
                        <EmptyPage content={studentError} />
                      )}
                      {!studentLoading &&
                        !studentError &&
                        studentQuestions.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 16,
                            }}
                          >
                            {studentResult &&
                              (() => {
                                const currentStudentDetails =
                                  completedStudents.find(
                                    (s) =>
                                      String(s.homework_completed_id) ===
                                      String(completedId),
                                  );
                                const studentName =
                                  currentStudentDetails?.name ||
                                  currentStudentDetails?.email ||
                                  "Student";
                                const studentEmail =
                                  currentStudentDetails?.email || "";
                                const studentRegNo =
                                  currentStudentDetails?.register_no || "";

                                const totalAttempted =
                                  studentResult.no_of_questions_attempted ??
                                  studentQuestions.length ??
                                  0;
                                const correctAnswersCount =
                                  studentResult.no_of_question_correct ??
                                  studentQuestions.filter((q) => {
                                    return typeof q.isCorrect === "boolean"
                                      ? q.isCorrect
                                      : String(q.user_answer || "")
                                          .trim()
                                          .toLowerCase() ===
                                          String(q.answer || "")
                                            .trim()
                                            .toLowerCase();
                                  }).length ??
                                  0;
                                const percentage =
                                  totalAttempted > 0
                                    ? Math.round(
                                        (correctAnswersCount / totalAttempted) *
                                          100,
                                      )
                                    : 0;
                                const feedbackItems =
                                  studentResult.feedback ||
                                  studentResult.feedbackItems ||
                                  [];

                                return (
                                  <>
                                    <div
                                      style={{
                                        backgroundColor:
                                          "var(--card-background, #fff)",
                                        border:
                                          "1px solid var(--divider-color, #e5e7eb)",
                                        borderRadius: "12px",
                                        padding: "20px 24px",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: "16px",
                                        marginBottom: "20px",
                                        boxShadow:
                                          "0 2px 8px rgba(0, 0, 0, 0.04)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "18px",
                                            fontWeight: "700",
                                            color: "var(--text-primary)",
                                          }}
                                        >
                                          {studentName}
                                        </div>
                                        {studentEmail && (
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              color: "var(--text-secondary)",
                                            }}
                                          >
                                            {studentEmail}
                                          </div>
                                        )}
                                        {studentRegNo && (
                                          <div
                                            style={{
                                              fontSize: "13px",
                                              color: "var(--text-secondary)",
                                              marginTop: "2px",
                                            }}
                                          >
                                            Register No: {studentRegNo}
                                          </div>
                                        )}
                                      </div>

                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "24px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <div style={{ textAlign: "right" }}>
                                          <div
                                            style={{
                                              fontSize: "13px",
                                              color: "var(--text-secondary)",
                                              fontWeight: "500",
                                            }}
                                          >
                                            Total Marks
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "18px",
                                              fontWeight: "800",
                                              color: "var(--text-primary)",
                                              marginTop: "2px",
                                            }}
                                          >
                                            {correctAnswersCount} /{" "}
                                            {totalAttempted}
                                          </div>
                                        </div>

                                        {feedbackItems.length > 0 ? (
                                          <button
                                            onClick={() =>
                                              setShowFeedbackModal(true)
                                            }
                                            style={{
                                              backgroundColor:
                                                "var(--color-primary, #4f46e5)",
                                              color: "#ffffff",
                                              border: "none",
                                              padding: "10px 20px",
                                              borderRadius: "8px",
                                              fontSize: "14px",
                                              fontWeight: "600",
                                              cursor: "pointer",
                                              transition: "all 0.2s ease",
                                              boxShadow:
                                                "0 4px 12px rgba(79, 70, 229, 0.2)",
                                            }}
                                          >
                                            Show Feedback
                                          </button>
                                        ) : (
                                          <span
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              backgroundColor: "#f9fafb",
                                              color: "#9ca3af",
                                              border: "1px dashed #d1d5db",
                                              padding: "10px 20px",
                                              borderRadius: "8px",
                                              fontSize: "14px",
                                              fontWeight: "500",
                                              userSelect: "none",
                                            }}
                                          >
                                            No feedback available
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <FeedbackPopup
                                      isOpen={showFeedbackModal}
                                      onClose={() =>
                                        setShowFeedbackModal(false)
                                      }
                                      feedbackItems={feedbackItems}
                                      percentage={percentage}
                                    />
                                  </>
                                );
                              })()}
                            {studentQuestions.map((q, idx) => (
                              <div
                                key={`stuq-${idx}`}
                                className="hd-question-card"
                              >
                                {(() => {
                                  const type = normalizeType(q);
                                  const data = [q];
                                  if (type === "trueorfalse")
                                    return (
                                      <TrueFalseViewer
                                        questionData={data}
                                        permission={false}
                                        title={"True / False"}
                                      />
                                    );
                                  if (type === "match")
                                    return (
                                      <MatchViewer
                                        questionData={
                                          Array.isArray(q?.pairs)
                                            ? {
                                                pairs: q.pairs,
                                                explanation:
                                                  q.explanation ||
                                                  (q.content &&
                                                    q.content.explanation) ||
                                                  "",
                                                categories:
                                                  q.categories ||
                                                  q.category ||
                                                  (q.content &&
                                                    q.content.categories) ||
                                                  "",
                                              }
                                            : data
                                        }
                                        permission={false}
                                        title={"Match the Following"}
                                      />
                                    );
                                  if (type === "fillups")
                                    return (
                                      <FillUpsViewer
                                        questionData={data}
                                        permission={false}
                                        title={"Fill in the Blanks"}
                                      />
                                    );
                                  return (
                                    <MCQViewer
                                      questionData={data}
                                      permission={false}
                                      title={"Multiple Choice Question"}
                                    />
                                  );
                                })()}
                                {q.user_answer !== undefined &&
                                  q.user_answer !== null &&
                                  (() => {
                                    const type = normalizeType(q);
                                    const isCorrect =
                                      typeof q.isCorrect === "boolean"
                                        ? q.isCorrect
                                        : String(q.user_answer || "")
                                            .trim()
                                            .toLowerCase() ===
                                          String(q.answer || "")
                                            .trim()
                                            .toLowerCase();
                                    const userAnswerText = getAnswerText(
                                      q.user_answer,
                                      q.options,
                                    );
                                    const correctAnswerText = getAnswerText(
                                      q.answer,
                                      q.options,
                                    );

                                    const displayUserAnswer = (() => {
                                      if (type === "match") {
                                        const leftCol =
                                          q.user_answer?.[0]?.values || [];
                                        const rightCol =
                                          q.user_answer?.[1]?.values || [];
                                        if (leftCol.length === 0)
                                          return "Not Answered";
                                        return (
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "4px",
                                              marginTop: "4px",
                                            }}
                                          >
                                            {leftCol.map((item, idx) => {
                                              const rightVal = rightCol[idx];
                                              const leftText =
                                                typeof item === "object"
                                                  ? item?.title ||
                                                    item?.value ||
                                                    ""
                                                  : item;
                                              const rightText =
                                                typeof rightVal === "object"
                                                  ? rightVal?.title ||
                                                    rightVal?.value ||
                                                    ""
                                                  : rightVal;
                                              return (
                                                <div
                                                  key={idx}
                                                  style={{ fontSize: "13px" }}
                                                >
                                                  • {leftText} ↔ {rightText}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      }
                                      if (!q.user_answer) return "Not Answered";
                                      const label = String(
                                        q.user_answer,
                                      ).toUpperCase();
                                      if (
                                        String(userAnswerText).toUpperCase() ===
                                        label
                                      ) {
                                        return userAnswerText;
                                      }
                                      return `${label} (${userAnswerText})`;
                                    })();

                                    const displayCorrectAnswer = (() => {
                                      if (type === "match") {
                                        const leftCol =
                                          q.pairs?.[0]?.values || [];
                                        const rightCol =
                                          q.pairs?.[1]?.values || [];
                                        return (
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "4px",
                                              marginTop: "4px",
                                            }}
                                          >
                                            {leftCol.map((item, idx) => {
                                              const rightVal = rightCol[idx];
                                              const leftText =
                                                typeof item === "object"
                                                  ? item?.title ||
                                                    item?.value ||
                                                    ""
                                                  : item;
                                              const rightText =
                                                typeof rightVal === "object"
                                                  ? rightVal?.title ||
                                                    rightVal?.value ||
                                                    ""
                                                  : rightVal;
                                              return (
                                                <div
                                                  key={idx}
                                                  style={{ fontSize: "13px" }}
                                                >
                                                  • {leftText} ↔ {rightText}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      }
                                      if (!q.answer) return "-";
                                      const label = String(
                                        q.answer,
                                      ).toUpperCase();
                                      if (
                                        String(
                                          correctAnswerText,
                                        ).toUpperCase() === label
                                      ) {
                                        return correctAnswerText;
                                      }
                                      return `${label} (${correctAnswerText})`;
                                    })();

                                    return (
                                      <div
                                        className="student-answer-review"
                                        style={{
                                          marginTop: "12px",
                                          padding: "12px 16px",
                                          borderRadius: "8px",
                                          backgroundColor: isCorrect
                                            ? "rgba(76, 175, 80, 0.08)"
                                            : "rgba(244, 67, 54, 0.08)",
                                          border: `1px solid ${isCorrect ? "rgba(76, 175, 80, 0.3)" : "rgba(244, 67, 54, 0.3)"}`,
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "6px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontWeight: "600",
                                              color: isCorrect
                                                ? "#2e7d32"
                                                : "#d32f2f",
                                              fontSize: "14px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px",
                                            }}
                                          >
                                            {isCorrect
                                              ? "✓ Correct Answer"
                                              : "✗ Incorrect Answer"}
                                          </span>
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            color: "var(--text-primary)",
                                          }}
                                        >
                                          <strong>Student's Answer:</strong>{" "}
                                          <span
                                            style={{
                                              color: isCorrect
                                                ? "#2e7d32"
                                                : "#d32f2f",
                                              fontWeight: "600",
                                            }}
                                          >
                                            {displayUserAnswer}
                                          </span>
                                        </div>
                                        {!isCorrect && (
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              color: "var(--text-primary)",
                                            }}
                                          >
                                            <strong>Correct Answer:</strong>{" "}
                                            <span
                                              style={{
                                                color: "#2e7d32",
                                                fontWeight: "600",
                                              }}
                                            >
                                              {displayCorrectAnswer}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              ) : questions && questions.length > 0 ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {/* Questions header actions */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                    }}
                  ></div>
                  {(qEdit ? qDraft : questions).map((q, idx) => (
                    <div key={idx} className="hd-question-card">
                      {/* <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ fontWeight: 600 }}>Q{idx + 1}</span>
                    </div> */}
                      <div
                        style={{
                          marginTop: 8,
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {(() => {
                          const type = normalizeType(q);
                          if (!qEdit) {
                            const data = [q];
                            if (type === "trueorfalse")
                              return (
                                <TrueFalseViewer
                                  key={`q-${idx}-${viewerVersion}-view`}
                                  questionData={data}
                                  permission={false}
                                  title={"True / False"}
                                />
                              );
                            if (type === "match")
                              return (
                                <MatchViewer
                                  key={`q-${idx}-${viewerVersion}-view`}
                                  questionData={
                                    Array.isArray(q?.pairs)
                                      ? {
                                          pairs: q.pairs,
                                          explanation:
                                            q.explanation ||
                                            (q.content &&
                                              q.content.explanation) ||
                                            "",
                                          categories:
                                            q.categories ||
                                            q.category ||
                                            (q.content &&
                                              q.content.categories) ||
                                            "",
                                        }
                                      : data
                                  }
                                  permission={false}
                                  title={"Match the Following"}
                                />
                              );
                            if (type === "fillups")
                              return (
                                <FillUpsViewer
                                  key={`q-${idx}-${viewerVersion}-view`}
                                  questionData={data}
                                  permission={false}
                                  title={"Fill in the Blanks"}
                                />
                              );
                            if (type === "analysis")
                              return (
                                <AnalysisComponent
                                  key={`q-${idx}-${viewerVersion}-view`}
                                  data={q}
                                  permission={false}
                                  title={"Analysis"}
                                />
                              );
                            return (
                              <MCQViewer
                                key={`q-${idx}-${viewerVersion}-view`}
                                questionData={data}
                                permission={false}
                                title={"Multiple Choice Question"}
                              />
                            );
                          }
                          const data = [q];
                          const handleViewerUpdate = (payload) => {
                            const items = Array.isArray(payload?.content)
                              ? payload.content
                              : [];
                            const first = items[0];
                            const updatedQs =
                              extractQuestionsFromViewerItem(first);
                            if (updatedQs.length === 0) return;
                            if (batchSaveId) {
                              setBatchAccum((prev) => {
                                const acc = { ...prev, [idx]: updatedQs[0] };
                                const received = Object.keys(acc).length;
                                if (received >= batchExpected) {
                                  const next = (questions || []).map((qq, i) =>
                                    Object.prototype.hasOwnProperty.call(acc, i)
                                      ? { ...qq, ...acc[i] }
                                      : qq,
                                  );
                                  persistQuestions(next);
                                  setBatchSaveId(null);
                                  setBatchExpected(0);
                                  setBatchCount(0);
                                  setBatchAccum({});
                                  setQEdit(false);
                                  setViewerVersion((v) => v + 1);
                                }
                                return acc;
                              });
                              setBatchCount((c) => c + 1);
                            } else {
                              const next = questions.map((qq, i) =>
                                i === idx ? { ...qq, ...updatedQs[0] } : qq,
                              );
                              persistQuestions(next);
                            }
                          };
                          if (type === "trueorfalse")
                            return (
                              <TrueFalseViewer
                                key={`q-${idx}-${viewerVersion}-edit`}
                                questionData={data}
                                permission={false}
                                startInEditMode={true}
                                allowEditWithoutPersist={true}
                                externalSaveSignal={saveNonce}
                                externalCancelSignal={cancelNonce}
                                onUpdate={handleViewerUpdate}
                                title={"True / False"}
                              />
                            );
                          if (type === "match")
                            return (
                              <MatchViewer
                                key={`q-${idx}-${viewerVersion}-edit`}
                                questionData={
                                  Array.isArray(q?.pairs)
                                    ? {
                                        pairs: q.pairs,
                                        explanation:
                                          q.explanation ||
                                          (q.content &&
                                            q.content.explanation) ||
                                          "",
                                        categories:
                                          q.categories ||
                                          q.category ||
                                          (q.content && q.content.categories) ||
                                          "",
                                      }
                                    : data
                                }
                                permission={false}
                                startInEditMode={true}
                                allowEditWithoutPersist={true}
                                externalSaveSignal={saveNonce}
                                externalCancelSignal={cancelNonce}
                                onUpdate={handleViewerUpdate}
                                title={"Match the Following"}
                              />
                            );
                          if (type === "fillups")
                            return (
                              <FillUpsViewer
                                key={`q-${idx}-${viewerVersion}-edit`}
                                questionData={data}
                                permission={false}
                                startInEditMode={true}
                                allowEditWithoutPersist={true}
                                externalSaveSignal={saveNonce}
                                externalCancelSignal={cancelNonce}
                                onUpdate={handleViewerUpdate}
                                title={"Fill in the Blanks"}
                              />
                            );
                          return (
                            <MCQViewer
                              key={`q-${idx}-${viewerVersion}-edit`}
                              questionData={data}
                              permission={false}
                              startInEditMode={true}
                              allowEditWithoutPersist={true}
                              externalSaveSignal={saveNonce}
                              externalCancelSignal={cancelNonce}
                              onUpdate={handleViewerUpdate}
                              title={"Multiple Choice Question"}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {editOpen && (
                <div
                  className="hd-modal-overlay"
                  onClick={() => setEditOpen(false)}
                >
                  <div
                    className="hd-modal-card"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h4 style={{ marginTop: 0 }}>
                      Edit Question {editIndex + 1}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      <label>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          Question
                        </div>
                        <textarea
                          rows={3}
                          value={editForm.question}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              question: e.target.value,
                            }))
                          }
                          style={{ width: "100%" }}
                        />
                      </label>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          Options
                        </div>
                        {(editForm.options || []).map((opt, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: 6,
                            }}
                          >
                            <input
                              type="radio"
                              name="correct"
                              checked={editForm.correctIndex === i}
                              onChange={() =>
                                setEditForm((f) => ({ ...f, correctIndex: i }))
                              }
                            />
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => {
                                const next = [...(editForm.options || [])];
                                next[i] = e.target.value;
                                setEditForm((f) => ({ ...f, options: next }));
                              }}
                              style={{ flex: 1 }}
                            />
                          </div>
                        ))}
                      </div>
                      <label>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          Explanation
                        </div>
                        <textarea
                          rows={3}
                          value={editForm.explanation}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              explanation: e.target.value,
                            }))
                          }
                          style={{ width: "100%" }}
                        />
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          className="action-button"
                          onClick={() => setEditOpen(false)}
                        >
                          Cancel
                        </button>
                        <button className="action-button" onClick={saveEdit}>
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default HomeworkDetail;
